import React, { createContext, useContext, ReactNode } from 'react';
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import axios from 'axios';
import { getApiUrl } from '../config/environments';

interface User {
  id: string;
  email: string;
  fullName: string;
  clerkId: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isSignedIn: boolean;
  signOut: () => void;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Configure axios defaults
axios.defaults.baseURL = getApiUrl();

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const { signOut: clerkSignOut, getToken } = useClerkAuth();

  const isLoading = !clerkLoaded;
  const isSignedIn = !!clerkUser;

  // Transform Clerk user to our User interface
  const user: User | null = clerkUser ? {
    id: clerkUser.id,
    email: clerkUser.primaryEmailAddress?.emailAddress || '',
    fullName: `${clerkUser.firstName || ''} ${clerkUser.lastName || ''}`.trim() || clerkUser.username || '',
    clerkId: clerkUser.id
  } : null;

  const signOut = React.useCallback(async () => {
    try {
      await clerkSignOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [clerkSignOut]);

  const getAuthToken = React.useCallback(async (): Promise<string | null> => {
    try {
      const token = await getToken();
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }, [getToken]);

  // Set up axios interceptor for automatic token attachment
  React.useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      async (config) => {
        const token = await getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  const contextValue = React.useMemo(() => ({
    user,
    isLoading,
    isSignedIn,
    signOut,
    getToken: getAuthToken
  }), [user, isLoading, isSignedIn, signOut, getAuthToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
